import type { City, Place } from '../world/city';
import type { PhysicsCity } from '../physics/world';
import type { Person, Population } from './population';

interface Clerk {
  person: Person;
  slot: Place;
  counter: Place;
  destination: Place;
  phase: 'serve' | 'inspect' | 'break';
  cycles: number;
  stockChecks: number;
  task: string;
  sequence: number;
  retry: number;
  reason: string;
}

/** Original finite employees walk through the public entrance and actually
 * attend checkout. Customers cannot complete purchases while the clerk is away. */
export class ShopStaff {
  units: Clerk[] = [];
  private counters = new Map<string, Clerk>();
  private epoch = 0;
  time = 0;
  constructor(
    readonly city: City,
    readonly physics: PhysicsCity,
    readonly population: Population,
  ) {
    this.reset();
  }
  clear(): void {
    for (const u of this.units) this.population.civil?.cancelTask(u.person, 'Shop staff lifecycle cleared');
    this.units = [];
    this.counters.clear();
    this.time = 0;
  }
  reset(): void {
    this.clear();
    this.epoch = this.physics.life.epoch;
    const civil = this.population.civil;
    if (!civil) return;
    for (const slot of this.city.places?.filter((p) => p.kind === 'staff' && p.serves) ?? []) {
      const counter = this.city.places!.find((p) => p.id === slot.serves),
        interior = this.city.interiors?.find((r) => r.buildingId === slot.buildingId);
      if (!counter || !interior || !civil.network.available(slot)) continue;
      const entranceIndex = this.units.filter((u) => u.slot.buildingId === slot.buildingId).length,
        person = this.population.createCrew(`crew:shop:${counter.id}`, {
          ...interior.entrance,
          x: interior.entrance.x + entranceIndex * 0.75,
        });
      civil.resume(person, 'street');
      const routine = civil.routines.get(person.id)!;
      routine.role = 'shopkeeper';
      routine.intent = 'Starting the checkout shift';
      routine.reason = 'Waiting for the assigned staff interaction';
      person.controlled = true;
      person.state = 'wait';
      person.layer = 'street';
      person.name = `${counter.name} · shopkeeper`;
      person.color = 0x8b9e8a;
      const unit: Clerk = {
        person,
        slot,
        counter,
        destination: slot,
        phase: 'serve',
        cycles: 0,
        stockChecks: 0,
        task: '',
        sequence: 0,
        retry: entranceIndex * 1.2,
        reason: 'Walking from the shop entrance to checkout',
      };
      this.units.push(unit);
      this.counters.set(counter.id, unit);
    }
  }
  status(counter: Place): 'open' | 'away' | 'closed' {
    const u = this.counters.get(counter.id),
      civil = this.population.civil;
    if (
      !u ||
      !civil ||
      !civil.network.available(u.slot) ||
      !civil.network.available(counter) ||
      ['incapacitated', 'falling', 'flee'].includes(u.person.state)
    )
      return 'closed';
    return u.person.state === 'serving customers' &&
      Math.hypot(u.person.p.x - u.slot.x, u.person.p.y - u.slot.y, u.person.p.z - u.slot.z) < 0.3
      ? 'open'
      : 'away';
  }
  update(dt: number): void {
    if (this.epoch !== this.physics.life.epoch || !this.population.civil) return;
    this.time += dt;
    const civil = this.population.civil;
    for (const u of this.units) {
      u.retry -= dt;
      if (['incapacitated', 'falling', 'flee'].includes(u.person.state)) {
        u.reason = 'Shop closed while the original employee needs safety or care';
        continue;
      }
      if (!civil.network.available(u.counter) || !civil.network.available(u.slot)) {
        civil.cancelTask(u.person, 'Checkout or its floor is damaged');
        u.task = '';
        u.retry = 5;
        u.reason = 'Checkout unavailable after damage';
        continue;
      }
      const current = civil.task(u.person),
        task = current?.token === u.task ? current : undefined;
      if (u.task && task?.state === 'active') {
        u.reason = task.reason;
        continue;
      }
      if (u.task && task?.state === 'completed') {
        if (u.phase === 'serve') {
          u.cycles++;
          const stocks = (this.city.places ?? []).filter(
              (p) =>
                p.buildingId === u.slot.buildingId &&
                p.floor === u.slot.floor &&
                p.kind === (u.cycles % 4 === 0 ? 'seat' : 'display') &&
                (!p.tenant || p.tenant === u.counter.tenant) &&
                civil.network.available(p),
            ),
            options = stocks.map(
              (p) => this.city.places!.find((q) => q.id === this.city.logistics?.servicePoints[p.id]) ?? p,
            ),
            next = options.find(
              (p) => !civil.reservations.holders(p.id, civil.time).length && civil.network.available(p),
            );
          u.phase = next ? (next.kind === 'seat' ? 'break' : 'inspect') : 'serve';
          u.destination = next ?? u.slot;
        } else {
          if (u.phase === 'inspect') u.stockChecks++;
          u.phase = 'serve';
          u.destination = u.slot;
        }
        u.task = '';
        u.retry = 0.2;
      } else if (u.task) {
        u.task = '';
        u.phase = 'serve';
        u.destination = u.slot;
        u.retry = 4;
      }
      if (u.retry > 0) continue;
      const token = `shop:${u.person.id}:${++u.sequence}`;
      if (
        civil.assignTask(
          u.person,
          u.destination,
          token,
          u.phase === 'serve' ? 'serving customers' : u.phase === 'inspect' ? 'inspecting' : 'resting',
          u.phase === 'serve' ? 120 : 8,
        )
      ) {
        u.task = token;
        u.reason = 'Following the actual reserved staff route';
      } else {
        u.retry = 3;
        u.reason = 'Waiting for a free staff interaction';
      }
    }
  }
  snapshot(): Record<string, number> {
    return {
      shopEmployees: this.units.length,
      staffedCounters: this.units.filter((u) => this.status(u.counter) === 'open').length,
      staffWorkSessions: this.units.reduce((n, u) => n + u.cycles, 0),
      staffStockChecks: this.units.reduce((n, u) => n + u.stockChecks, 0),
    };
  }
  inspect(): unknown[] {
    return this.units.map((u) => ({
      person: u.person.id,
      counter: u.counter.id,
      state: this.status(u.counter),
      phase: u.phase,
      destination: u.destination.id,
      cycles: u.cycles,
      stockChecks: u.stockChecks,
      reason: u.reason,
      position: u.person.p.toArray(),
    }));
  }
}
